import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { Reveal } from '@/components/Reveal';
import { indiaSolutions } from '@/data/indiaServices';

const whyIndia = [
  'Dedicated team that understands the Indian business landscape',
  'Bilingual support in English and Hindi where required',
  'Cost-effective solutions tailored for Indian business budgets',
  'Local market knowledge combined with international quality standards',
  'Flexible engagement models — project, retainer or ongoing support',
];

export default function IndiaSolutions() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="India — Digital & Business Solutions | NKP VENTURES"
        description="NKP VENTURES provides digital solutions, creative services, business support and e-commerce support for Indian businesses."
        canonical="https://nkpventures.com/solutions/india"
      />

      {/* Hero */}
      <section className="bg-[#0B3F3B] pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="inline-block bg-white/20 text-white text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full mb-6">India</span>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Digital & Business Solutions
              <br />
              <span className="text-[#D4AF37]">for Indian Businesses.</span>
            </h1>
            <p className="text-lg text-white/75 max-w-2xl leading-relaxed mb-10">
              Helping Indian businesses build their brand, strengthen their digital presence and streamline their day-to-day operations — with the quality standards of an international partner.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0B3F3B] font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Get in Touch <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-medium px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                All Solutions
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24 bg-[#F7FAFC]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="mb-14">
              <p className="overline-label text-[#0B3F3B] mb-3">Our India Offering</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A1628]">
                Four areas. One integrated partner.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {indiaSolutions.map((cat, i) => (
              <Reveal key={cat.id} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-8 border border-[#E5E7EB] hover:border-[#0B3F3B]/30 hover:shadow-lg transition-all duration-300 h-full">
                  <span className="text-xs font-semibold tracking-widest uppercase text-[#0B3F3B] bg-teal-50 px-3 py-1 rounded-full">
                    {cat.title}
                  </span>
                  <p className="text-[#64748B] italic text-sm mt-3 mb-5">{cat.tagline}</p>
                  <p className="text-[#374151] text-sm leading-relaxed mb-6">{cat.description}</p>
                  <div className="space-y-3">
                    {cat.services.map((svc) => (
                      <div key={svc.title} className="flex gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#0B3F3B] mt-2 flex-shrink-0" />
                        <div>
                          <p className="text-[#0A1628] font-semibold text-sm">
                            {svc.serviceSlug ? (
                              <Link to={`/services/${svc.serviceSlug}`} className="hover:text-[#0B3F3B] hover:underline transition-colors">
                                {svc.title}
                              </Link>
                            ) : svc.title}
                          </p>
                          <p className="text-[#6B7280] text-xs mt-0.5 leading-snug">{svc.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why NKP for India */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <p className="overline-label text-[#0B3F3B] mb-3">Why NKP VENTURES</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A1628] mb-6">
                An Indian business partner with international standards.
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-8">
                We understand the Indian business environment. We combine local knowledge with the professional standards and operational discipline required to help you grow — whether your clients are in India or internationally.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#0B3F3B] hover:bg-[#0D4F4A] text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Start a Conversation <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="space-y-4">
                {whyIndia.map((point) => (
                  <li key={point} className="flex gap-3 items-start">
                    <CheckCircle className="w-5 h-5 text-[#0B3F3B] mt-0.5 flex-shrink-0" />
                    <span className="text-[#374151] leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B3F3B]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl font-bold text-white mb-4">
              Ready to build something better?
            </h2>
            <p className="text-white/70 mb-8">
              Tell us about your business. We'll propose the right solution.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#0B3F3B] font-semibold px-8 py-4 rounded-full transition-colors duration-200"
            >
              Let's Talk <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
